import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { pick } from "lodash";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import { applicationTypes } from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import InputFieldText from "Components/Common/InputFieldText";
import {
  useLazyGetDemandEstimationByApplicationIdQuery,
  usePostDemandEstimationMutation,
} from "apis/DemandEstimation";
import { useGetSourceOfPowersQuery } from "apis/TechnicalSanction";

import { filterEmptyStrings, onlyNumberKeys } from "utils";

const editableApplicationTypeId = [
  applicationTypes["Permanent Connection"],
  applicationTypes["Temporary Connection"],
];

const DemandEstimation = ({
  applicationId,
  handleClose,
  applicationTypeId,
  isDemandEstimationModalOpen,
  isSubStationFieldRequired,
  t,
}) => {
  const isMobileDevice = useMediaQuery("(max-width: 768px)");
  const [
    getDemandEstimationDataById,
    {
      data: { data: demandEstimationData = {} } = {},
      isLoading: isDemandEstimationDataLoading,
    },
  ] = useLazyGetDemandEstimationByApplicationIdQuery();

  const { data: { data: sourceOfPowerOptions } = {} } =
    useGetSourceOfPowersQuery();

  //add demand estimation api
  const [addDemandEstimation, { isLoading }] =
    usePostDemandEstimationMutation();

  const isDisabled = useMemo(
    () => !editableApplicationTypeId.includes(applicationTypeId),
    [applicationId]
  );

  useEffect(() => {
    if (applicationId) {
      getDemandEstimationData(applicationId);
    }
  }, [applicationId]);

  const getDemandEstimationData = async (id) => {
    try {
      await getDemandEstimationDataById(id);
    } catch (e) {}
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      meterInstallationFloorId:
        demandEstimationData?.meterInstallationFloorId ?? null,
      sourceOfPower: demandEstimationData?.sourceOfPowerId ?? 0,
      agreementFee: demandEstimationData?.agreementFee ?? 0,
      isSubStationFieldRequired: isSubStationFieldRequired,
      isCommonArea: demandEstimationData?.isCommonArea ?? false,
    },
    validationSchema: Yup.object({
      meterInstallationFloorId:
        !!demandEstimationData?.utilityConsumerFloors?.length &&
        Yup.string()
          .nullable()
          .required("Meter Installation Floor is required"),
      sourceOfPower: Yup.string().when("isSubStationFieldRequired", {
        is: true,
        then: (schema) => schema.required("Source of power is required"),
        otherwise: (schema) => schema,
      }),
      agreementFee: Yup.number().required("Agreement Fee is required"),
    }),
    onSubmit: async (values) => {
      const query = filterEmptyStrings(
        pick(values, [
          "meterInstallationFloorId",
          "sourceOfPower",
          "agreementFee",
          "isCommonArea",
        ])
      );
      try {
        await addDemandEstimation(null, applicationId, query);
        handleClose();
      } catch (err) {}
    },
  });

  const {
    values: { isCommonArea },
    setFieldValue,
  } = formik;

  if (isDemandEstimationDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen={isDemandEstimationModalOpen}
      handleClose={handleClose}
      title={t("Issue Demand Estimation")}
      maxWidth="md"
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container className="mt-3" columnSpacing={2}>
          {!!demandEstimationData?.utilityConsumerFloors?.length && (
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Meter Installation Floor") + "*"}
                name="meterInstallationFloorId"
                formik={formik}
                disabled={isDisabled}
                options={demandEstimationData?.utilityConsumerFloors || []}
                getOptionLabel={({ name }) => name}
                getOptionValue={({ id }) => id}
              />
            </Grid>
          )}
          {isSubStationFieldRequired && (
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Source Of Power") + "*"}
                name="sourceOfPower"
                formik={formik}
                disabled={isDisabled}
                options={sourceOfPowerOptions || []}
                getOptionLabel={({ name }) => name}
                getOptionValue={({ id }) => id}
              />
            </Grid>
          )}
          {!isDisabled && (
            <Grid item xs={12} md={4} sm={6}>
              <InputFieldText
                label={t("Agreement Fee") + "*"}
                name="agreementFee"
                formik={formik}
                type="number"
                InputProps={{
                  inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                }}
                placeholder="Enter Agreement Fee"
              />
            </Grid>
          )}
          <Grid item xs={12} md={4} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!isCommonArea}
                  disabled={isDisabled}
                  onChange={(_, isChecked) =>
                    setFieldValue("isCommonArea", isChecked)
                  }
                />
              }
              label={t("Common Area")}
            />
          </Grid>
          <Grid className="d-block ms-auto" mt={1}>
            <Button
              color="primary"
              size="small"
              className={isMobileDevice ? "mt-2" : "mb-2"}
              variant="outlined"
              type="submit"
              disabled={isLoading}
            >
              <div className="d-flex align-items-center gap-1">
                <strong>{t("Save")}</strong>
                {isLoading && <CircularProgress size={20} />}
              </div>
            </Button>
          </Grid>
        </Grid>
      </form>
    </Modal>
  );
};

DemandEstimation.propTypes = {
  applicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDemandEstimationModalOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  applicationTypeId: PropTypes.number,
  isSubStationFieldRequired: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(DemandEstimation);
